import { create } from 'zustand';
import { getPersistenceNamespace, onPersistenceNamespaceChange } from '../../stores/helpers/persistenceHelpers';
import {
  ackNotices, claimNotices, isOwnerView, listBuilds, onBuildMutation,
  type AnyBuild, type BuildList, type BuildView, type Notice,
} from './buildsApi';
import { nextDelay } from './usePolled';

/**
 * The viewer's own builds for the shell (#1625, R6): the sidebar's spinner and count, Home's cards and the build
 * toasts read this one store, filled by one poller from `GET /api/builder/jobs?mine=1` (the status read model's brief
 * projection, W19) — every 10 s while one of them runs or waits (60 s hidden), none otherwise; again on focus, when
 * the tab shows, on any build mutation (`onBuildMutation`), and when a build page opens.
 *
 * Fenced (N3): every read and claim names the persistence namespace (account and workspace) and this store's
 * generation when it is sent, and is applied only if both still hold; a switch clears everything and drops what is
 * in flight. A toast is one claim's (R3): it is acked only once it is on screen, and a dropped one is never acked,
 * so its notices are offered again when their lease lapses.
 */

export const SUMMARY_MS = { live: 10_000, hidden: 60_000, leaseHold: 55_000 } as const;

/** `seq`: when its claim's reply came, on the same clock as `dataSeq` — a list read SENT after it is fresher. */
export interface HeldToast { notice: Notice; more: number; lease: string; namespace: string | null; generation: number; claimedAt: number; seq: number }

interface SummaryState {
  generation: number;
  data: BuildList | null;
  /** When the read that brought `data` was sent (`clock`): a count is shown only from a read newer than its claim. */
  dataSeq: number;
  toast: HeldToast | null;
  /** The build page on screen (its notices are told there, not by a toast). */
  onPage: string | null;
}

export const useBuildsSummary = create<SummaryState>(() => ({ generation: 0, data: null, dataSeq: 0, toast: null, onPage: null }));

let clock = 0;

let started = false;
let etag: string | null = null;
let inFlight = false;
let owed = false;
let claiming = false;
let failures = 0;
let timer: ReturnType<typeof setTimeout> | undefined;
let offs: Array<() => void> = [];

const visible = () => typeof document === 'undefined' || !document.hidden;
const fence = () => ({ namespace: getPersistenceNamespace(), generation: useBuildsSummary.getState().generation });
const holds = (f: { namespace: string | null; generation: number }) =>
  started && f.namespace === getPersistenceNamespace() && f.generation === useBuildsSummary.getState().generation;

/** How far a build has got: its state only moves forward (queued → running → stopping → ended → published). */
const STAGE: Record<string, number> = { queued: 0, running: 1, ready: 3, stopped: 3, failed: 3, published: 4 };
export const stage = (b: { state: string; stopping?: boolean }) => (STAGE[b.state] ?? 0) + (b.state === 'running' && b.stopping ? 1 : 0);

/**
 * `next` with no build moved backwards against `prev` (#1625 R6): a read that left before a mutation's reply but came
 * back after it keeps the reply's state. Monotone, whatever order the replies arrive in.
 */
export function forward(prev: BuildList | null, next: BuildList): BuildList {
  const before = new Map((prev?.jobs || []).filter(isOwnerView).map((b) => [b.job_id, b]));
  return { ...next, jobs: next.jobs.map((b) => {
    const old = before.get(b.job_id);
    return old && isOwnerView(b) && stage(old) > stage(b) ? { ...b, state: old.state, stopping: old.stopping } : b;
  }) };
}

/** A mutation's reply reports a build's state: it is in the summary at once, never moving it backwards. */
function told(build: AnyBuild): void {
  if (!isOwnerView(build)) return;
  const data = useBuildsSummary.getState().data;
  const at = data ? data.jobs.findIndex((b) => b.job_id === build.job_id) : -1;
  const jobs = data ? [...data.jobs] : [];
  if (at >= 0) jobs[at] = build; else jobs.unshift(build);
  const next = forward(data, { jobs, running: data ? data.running : 0, max_running: data ? data.max_running : 2, as_of: build.as_of });
  useBuildsSummary.setState({ data: next });
}

export const ownBuilds = (data: BuildList | null): BuildView[] => (data ? data.jobs.filter(isOwnerView) : []);
export const isLive = (b: { state: string }) => b.state === 'running' || b.state === 'queued';
/**
 * A build's questions waiting on its owner: only while the build can still use the answers (a published build can: an
 * answer feeds a Check and a republish). A failed or stopped build keeps its list on its page, and asks nothing of anyone.
 */
export const waitingQuestions = (b: Pick<BuildView, 'state' | 'questions'> & { stopping?: boolean }): number =>
  (['queued', 'running', 'ready', 'published'].includes(b.state) && !b.stopping ? Math.max(0, b.questions?.open ?? 0) : 0);
export const openQuestions = (data: BuildList | null): number =>
  ownBuilds(data).reduce((n, b) => n + waitingQuestions(b), 0);

function schedule() {
  clearTimeout(timer);
  const data = useBuildsSummary.getState().data;
  const live = ownBuilds(data).some(isLive);
  if (!started || (!live && failures === 0)) return;
  const base = !visible() ? SUMMARY_MS.hidden : SUMMARY_MS.live;
  timer = setTimeout(() => { void read(); }, nextDelay(base, { hidden: false, failures }));
}

/** Read the viewer's builds now (one request at a time: a read asked for while one is out is owed after it). */
export async function read(): Promise<void> {
  if (!started) return;
  if (inFlight) { owed = true; return; }
  inFlight = true;
  owed = false;
  const f = fence();
  const sent = ++clock;
  let changedBody = false;
  try {
    const got = await listBuilds(etag, { mine: true });
    if (holds(f)) {                           // else dropped: a switch came while it was out
      failures = 0;
      if (got.changed) {
        useBuildsSummary.setState((st) => ({ data: forward(st.data, got.data), dataSeq: sent }));
        changedBody = true;
      } else {
        useBuildsSummary.setState({ dataSeq: sent });       // a 304: what is shown is as fresh as this read
      }
      etag = got.etag;
    }
  } catch {
    if (holds(f)) failures += 1;
  } finally {
    inFlight = false;
  }
  if (owed) { void read(); return; }          // asked for meanwhile (a mutation, a switch): read again first
  if (!holds(f)) return;
  if (changedBody) void claim();
  schedule();
}

/** Ask for a toast: only while visible and none is held (one claim, one toast, one ack). */
export async function claim(): Promise<void> {
  if (!started || claiming || !visible() || useBuildsSummary.getState().toast) return;
  claiming = true;
  const f = fence();
  try {
    const got = await claimNotices(useBuildsSummary.getState().onPage);
    if (!holds(f) || !got.toast || !got.lease) return;       // dropped: never acked, offered again after its lease
    useBuildsSummary.setState({ toast: { notice: got.toast, more: got.more, lease: got.lease, ...f, claimedAt: Date.now(), seq: ++clock } });
  } catch {
    /* nothing claimed is shown; its lease lapses */
  } finally {
    claiming = false;
  }
}

/** The held toast is on screen: ack its lease (every notice it names is shown). */
export function shown(toast: HeldToast): void {
  if (!holds(toast)) return;
  void ackNotices(toast.lease).catch(() => {});
}

/** The toast went (timed out, dismissed or acted on): ask for the next one. */
export function closeToast(toast: HeldToast | null): void {
  const held = useBuildsSummary.getState().toast;
  if (!held || (toast && held.lease !== toast.lease)) return;
  useBuildsSummary.setState({ toast: null });
  void claim();
}

/** A held toast that never got on screen before its lease lapses is dropped unacked (it will be offered again). */
export function dropIfStale(now = Date.now()): void {
  const held = useBuildsSummary.getState().toast;
  if (held && now - held.claimedAt > SUMMARY_MS.leaseHold) useBuildsSummary.setState({ toast: null });
}

export function setOnPage(jobId: string | null): void {
  if (useBuildsSummary.getState().onPage === jobId) return;
  useBuildsSummary.setState({ onPage: jobId });
  if (jobId) void read();
}

/** A switch of account or workspace: everything goes, and nothing in flight from before is applied. */
export function reset(): void {
  clearTimeout(timer);
  etag = null;
  owed = false;
  failures = 0;
  useBuildsSummary.setState((s) => ({ generation: s.generation + 1, data: null, dataSeq: 0, toast: null, onPage: null }));
  if (started) void read();
}

const onShow = () => { if (visible()) { void read(); void claim(); } };

/** Start the shell's poller (with AGENT_BUILDER on); returns its stop. */
export function startSummary(): () => void {
  if (started) return stopSummary;
  started = true;
  document.addEventListener('visibilitychange', onShow);
  window.addEventListener('focus', onShow);
  offs = [onBuildMutation((build, namespace) => { if (build && namespace === getPersistenceNamespace()) told(build); void read(); })];
  void read();
  void claim();
  return stopSummary;
}

export function stopSummary(): void {
  if (!started) return;
  started = false;
  clearTimeout(timer);
  document.removeEventListener('visibilitychange', onShow);
  window.removeEventListener('focus', onShow);
  offs.forEach((off) => off());
  offs = [];
  etag = null;
  inFlight = false;
  owed = false;
  claiming = false;
  failures = 0;
  useBuildsSummary.setState((s) => ({ generation: s.generation + 1, data: null, dataSeq: 0, toast: null, onPage: null }));
}

onPersistenceNamespaceChange(() => reset());
